import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { catchError, EMPTY, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { SessionService } from '@pgaoc/shared-lib';
import { InternalAuthRepository } from '../repository/internal-auth-repository';

@Injectable()
export class InternalAuthInterceptor implements HttpInterceptor {
  private sessionService = inject(SessionService);
  private internalAuthRepository = inject(InternalAuthRepository);

  private ignoredRequests = ['/refreshsession'];

  /**
   * Interceptor for handling authentication and session refresh in HTTP requests.
   * Adds authorization headers to requests and refreshes session when needed.
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isIgnored = this.ignoredRequests.some(p => request.url.toLowerCase().includes(p.toLowerCase()));

    if (isIgnored) {
      const reqNoAuth = request.clone({
        headers: this.createHeaders(undefined, request.headers),
      });
      return next.handle(reqNoAuth);
    }

    return this.sessionService.authenticatedUser$.pipe(
      take(1),
      switchMap(user => {
        const reqWithAuth = request.clone({
          headers: this.createHeaders(user?.userSession?.accessToken, request.headers),
        });

        return next.handle(reqWithAuth).pipe(
          catchError(err => {
            if (err instanceof HttpErrorResponse && err.status === 401) {
              return this.handle401(reqWithAuth, next);
            }
            return throwError(() => err);
          }),
        );
      }),
    );
  }

  /**
   * Handles session refresh in case of a 401 error by ensuring single refresh
   * and retrying the original request with the new token.
   */
  private handle401(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.sessionService.refreshingSession$.pipe(
      take(1),
      switchMap(isRefreshing => {
        if (!isRefreshing) {
          return this.internalAuthRepository.refreshSession().pipe(
            take(1),
            switchMap(user => {
              const token = user?.userSession?.accessToken;
              if (!token) {
                return EMPTY;
              }
              const retried = request.clone({
                headers: this.createHeaders(token, request.headers, true),
              });
              return next.handle(retried);
            }),
            catchError(error => {
              if (error instanceof HttpErrorResponse && error.status === 400) {
                this.sessionService.destroySession.next(true);
              }
              return throwError(() => error);
            }),
          );
        }

        return this.sessionService.refreshingSession$.pipe(
          filter(r => !r),
          take(1),
          switchMap(() => this.sessionService.authenticatedUser$.pipe(take(1))),
          switchMap(user => {
            const token = user?.userSession?.accessToken;
            if (!token) {
              return EMPTY;
            }
            const retried = request.clone({
              headers: this.createHeaders(token, request.headers, true),
            });
            return next.handle(retried);
          }),
        );
      }),
    );
  }

  private createHeaders(token: string | undefined, headers: HttpHeaders, force = false): HttpHeaders {
    if (!headers.get('Content-Type')) {
      headers = headers.append('Content-Type', 'application/json');
    }
    if (!headers.get('Accept')) {
      headers = headers.append('Accept', 'application/json');
    }
    if ((!headers.get('Authorization') && !!token) || force) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
}
